import React from "react";
import { EventsContainer, HistoricalContainer } from "../style";
import { Historical, historicalEventTypeMap } from "@customTypes/responses/lawsuit";
import { formatISODate, formatStringDate } from "@helpers/dateHelpers";
import { DateText, NormalText } from "@components/styles/dataDisplay";
import EventItem from "./EventItem";
import uuid from "react-uuid";
import HistoricalIcon from "./MovementIcon";
import { Modal } from "@components/Modal";
import { CustomHeading } from "@pages/style";
import { hiddenTitlesByType } from "./historicalConstants";
import HistoricalDescriptionTranslation from "./HistoricalDescriptionTranslation";
import HistoricalDescription from "./HistoricalDescription";
import sanitizeHtml from "sanitize-html";
import { FaMagic } from "@react-icons/all-files/fa/FaMagic";

type MovementHistoricalItemProps = {
  historical: Historical;
};

const writeSubtitle = (historical: Historical) => {
  switch (historical.type) {
    case "AUTOMATIC_LAWSUIT": {
      return "Andamento automático";
    }
    case "CLIPPING": {
      return "Publicação";
    }
    case "DONE_TASK": {
      return `Tarefa concluída por ${historical.responsibleName}`;
    }
    case "MANUALLY": {
      return `${historical.responsibleName} registrou`;
    }
    default:
      "Sem tipo";
  }
};

const MovementHistoricalItem: React.FC<MovementHistoricalItemProps> = ({ historical }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const singleEvent = historical.events?.length === 1;

  const shouldShowIATrasnlation =
    historical.descriptionTranslation && historical.type === "AUTOMATIC_LAWSUIT";

  return (
    <HistoricalContainer>
      <DateText noMargin style={{ marginTop: 4 }}>
        {formatISODate(historical.date)}
      </DateText>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          columnGap: 12,
          alignItems: "center",
          gridArea: "title",
        }}
      >
        <HistoricalIcon historicalType={historical.type} />
        <NormalText color="gray" semiBold>
          {writeSubtitle(historical)}
        </NormalText>
      </div>
      <div style={{ marginLeft: 25.5, gridArea: "description", wordBreak: "break-word" }}>
        <HistoricalDescription eventDescription={historical.description} openModal={openModal} />
        <HistoricalDescriptionTranslation
          openModal={openModal}
          descriptionTranslation={historical.descriptionTranslation}
        />
      </div>
      {historical?.events?.length > 0 && (
        <EventsContainer>
          {historical?.events?.map((event, index) => (
            <EventItem
              key={uuid()}
              event={event}
              withLine={singleEvent ? true : index + 1 < historical?.events.length}
              single={singleEvent}
            />
          ))}
        </EventsContainer>
      )}
      <Modal onClose={closeModal} blackout isOpen={isModalOpen}>
        <CustomHeading semiBold>{historicalEventTypeMap[historical.type]}</CustomHeading>
        {historical.date && (
          <div style={{ display: "flex", columnGap: 8 }}>
            <NormalText noMargin semiBold>
              Data:
            </NormalText>
            <NormalText noMargin color="gray">
              {formatStringDate(historical.date)}
            </NormalText>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", rowGap: 8 }}>
          {!hiddenTitlesByType.includes(historical.type) && (
            <NormalText semiBold>{writeSubtitle(historical)}:</NormalText>
          )}
          <NormalText
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(historical.description),
            }}
          />
          {shouldShowIATrasnlation && (
            <>
              <div style={{ display: "flex", columnGap: 8, alignItems: "center", marginTop: 14 }}>
                <FaMagic size={12} />
                <NormalText semiBold>Interpretação da Inteligência Artificial</NormalText>
              </div>
              <NormalText
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(historical.descriptionTranslation),
                }}
              />
            </>
          )}
        </div>
      </Modal>
    </HistoricalContainer>
  );
};

export default MovementHistoricalItem;
